import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { getUserProfile } from 'modules/auth/selectors';
import { memberApiDefinitions } from 'modules/service/types';
import { useAppDispatch } from 'modules/common/hooks';
import { PERMISSIONS } from 'modules/common/constants';
import { formatString } from 'modules/common/utils';
import Table from 'modules/common/components/Table';
import { ROLE_TYPE } from 'modules/admin/constants';
import RolesChips from 'modules/admin/components/RolesChips';
import { OWNERS_ROUTES } from 'modules/admin/owners/constants';
import { goToOwnerMembershipsPage } from 'modules/admin/owners/actions';

import local from '../GroupDetailsLayout/local.module.scss';

interface GroupOwnersTableProps {
    items: Array<memberApiDefinitions['GroupOwnerDetails']>;
    isLoaded: boolean;
}

const LIST_SKELETON_ROWS_COUNT = 5;

const GroupOwnersTable: FunctionComponent<GroupOwnersTableProps> = ({ items, isLoaded }) => {
    const dispatch = useAppDispatch();
    const { owners } = useSelector(getUserProfile);

    const onTableRowClick = useCallback(({ id }) => {
        if (isLoaded) {
            const canViewOwner = owners?.find(({ id: ownerId }) => id === ownerId)?.permissions.includes(PERMISSIONS.MEMBERSHIPS.VIEW);

            if (canViewOwner) {
                dispatch(goToOwnerMembershipsPage(id));
            }
        }
    }, [ isLoaded, owners ]);

    const tableConfig = useMemo(() => [
        {
            Header: <FormattedMessage id='common.name' />,
            id: 'name',
            accessor: ({ id, name }) => {
                const canViewOwner = owners?.find(({ id: ownerId }) => id === ownerId)?.permissions.includes(PERMISSIONS.MEMBERSHIPS.VIEW);

                return canViewOwner ? (
                    <a href={`#${formatString(OWNERS_ROUTES.DETAILS, id)}`} className={local.name}>
                        {name}
                    </a>
                ) : name;
            },
            width: '25%',
            skeletonWidth: '100%',
            showTooltip: true
        },
        {
            Header: <FormattedMessage id='admin.roles' />,
            id: 'roles',
            accessor: ({ roles }) => (
                <RolesChips
                    editable={false}
                    type={ROLE_TYPE.OWNER}
                    selectedRoles={roles}
                />
            ),
            skeletonWidth: '100%'
        }
    ], [ owners ]);

    return (
        <Table
            columns={tableConfig}
            data={items}
            skeletonRowsCount={LIST_SKELETON_ROWS_COUNT}
            isLoaded={isLoaded}
            onRowClick={onTableRowClick}
        />
    );
};

export default GroupOwnersTable;
